import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ArrowLeftRight, HeartPulse, ShieldAlert } from 'lucide-react';
import Header from '@/components/Header';
import { Button } from '@/components/ui/button';
import { PatternSelector } from '@/components/ekg/PatternSelector';
import { EcgViewer } from '@/components/ekg/EcgViewer';
import { ClinicalNotes } from '@/components/ekg/ClinicalNotes';
import { BundleComparison } from '@/components/ekg/BundleComparison';
import { getPattern } from '@/data/ekg';
import '@/styles/ekg.css';

export default function EkgHelper() {
  const [params, setParams] = useSearchParams();
  const pattern = getPattern(params.get('befund')) || getPattern('rbbb');
  const [compareOpen, setCompareOpen] = useState(false);
  const selectPattern = id => setParams({ befund: id });
  useEffect(() => {
    const previous = document.title;
    document.title = 'EKG-Hilfe · Anamnese Generator';
    window.scrollTo({ top: 0, behavior: 'instant' });
    return () => { document.title = previous; };
  }, []);

  return (
    <div className="min-h-screen bg-background ekg-page">
      <Header />
      <main className="ekg-main" data-testid="ekg-helper-page">
        <div className="ekg-page-heading">
          <div>
            <p className="ekg-eyebrow"><HeartPulse size={16} />EKG-NACHSCHLAGEWERK</p>
            <h1 className="text-4xl sm:text-5xl font-bold mt-2" data-testid="ekg-page-title">EKG-Hilfe</h1>
            <p className="text-sm text-muted-foreground mt-3">Rhythmus. Überleitung. ST-Strecke.</p>
          </div>
          <Button variant="outline" className="gap-2 h-11 bg-white" onClick={() => setCompareOpen(true)} data-testid="ekg-compare-button"><ArrowLeftRight size={17} />RSB / LSB vergleichen</Button>
        </div>
        <div className="ekg-safety-note" role="note" data-testid="ekg-safety-note"><ShieldAlert size={18} className="shrink-0 mt-0.5" /><p>Lern- und Orientierungshilfe für Erwachsene, keine Diagnose. Schematische Beispiele zeigen typische, nicht alle möglichen Formen. Klinischen Zustand und vollständiges EKG beurteilen; Notfallversorgung nicht verzögern.</p></div>
        <div className="ekg-workspace">
          <PatternSelector selectedId={pattern.id} onSelect={selectPattern} />
          <article className="ekg-workbench" data-testid="ekg-selected-pattern">
            <header className="ekg-pattern-heading">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <span className={`ekg-category-tag ${pattern.category === 'emergency' ? 'is-urgent' : ''}`} data-testid="ekg-pattern-tag">{pattern.tag}</span>
                <span className="text-xs text-muted-foreground">LEHRBEISPIEL</span>
              </div>
              <h2 className="text-lg font-bold" data-testid="ekg-pattern-title">{pattern.title}</h2>
              <p className="text-sm text-muted-foreground leading-relaxed mt-2" data-testid="ekg-pattern-summary">{pattern.summary}</p>
            </header>
            <div className="ekg-detail-layout">
              <EcgViewer key={pattern.id} pattern={pattern} />
              <ClinicalNotes pattern={pattern} />
            </div>
          </article>
        </div>
        <footer className="ekg-footer">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-teal-800 hover:underline" data-testid="ekg-return-link"><ArrowLeft size={16} />Zum Anamnese-Generator</Link>
          <p className="text-xs text-muted-foreground" data-testid="ekg-editorial-note">Fachquellen: LITFL ECG Library · abgerufen 09.10.2026. Eigenständige schematische Lehrgrafiken; nicht klinisch validiert.</p>
          <nav className="flex gap-4 text-xs text-muted-foreground" aria-label="Rechtliche Informationen"><Link to="/impressum" data-testid="ekg-imprint-link">Impressum</Link><Link to="/datenschutz" data-testid="ekg-privacy-link">Datenschutz</Link></nav>
        </footer>
      </main>
      <BundleComparison open={compareOpen} onOpenChange={setCompareOpen} onSelect={selectPattern} />
    </div>
  );
}